import React, { useId, useState } from 'react';
import paypalLogo from '../../assets/images/payment/paypal.png';
import paystackLogo from '../../assets/images/payment/paystack.svg';
import { ApplePayMark, PaystackStripeBrand } from './PaymentProviderBrand';

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${
        expanded ? 'rotate-180' : ''
      }`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function AccordionSection({
  expanded,
  onToggle,
  header,
  panelId,
  headerId,
  children,
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        id={headerId}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-4 text-left transition-colors sm:px-5 min-h-[64px] ${
          expanded
            ? 'border-b border-slate-200 bg-white'
            : 'hover:bg-slate-50'
        }`}
      >
        {header}
        <ChevronIcon expanded={expanded} />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        hidden={!expanded}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-4 sm:px-5 sm:py-5">
            {expanded ? children : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function MorePaymentOptionsSection({
  expanded,
  onToggle,
  panelId,
  headerId,
  children,
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        id={headerId}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-4 text-left transition-colors sm:px-5 min-h-[44px] ${
          expanded
            ? 'border-b border-slate-200 bg-white'
            : 'hover:bg-slate-50'
        }`}
      >
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">More payment options</p>
        </div>
        <ChevronIcon expanded={expanded} />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        hidden={!expanded}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-4 sm:px-5 sm:py-5">
            {expanded ? children : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** PayPal disabled — show Paystack as primary method with ZAR visible in header. */
function PaystackPrimarySection({ paystackQuote, paystackSection, baseId }) {
  const zarLabel = paystackQuote?.amount_display || '—';

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div
        id={`${baseId}-paystack-primary-header`}
        className="flex w-full items-center gap-3 border-b border-slate-200 px-4 py-4 sm:px-5 min-h-[64px]"
      >
        <img
          src={paystackLogo}
          alt=""
          className="h-8 w-auto shrink-0 object-contain"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <p className="font-semibold text-slate-900">Card or bank</p>
              <p className="text-xs text-slate-500">ZAR</p>
              <PaystackStripeBrand size="header" className="text-slate-700" />
            </div>
            <span className="shrink-0 text-sm font-semibold text-slate-900 pt-0.5">{zarLabel}</span>
          </div>
        </div>
      </div>
      <div
        id={`${baseId}-paystack-primary-panel`}
        role="region"
        aria-labelledby={`${baseId}-paystack-primary-header`}
        className="px-4 py-4 sm:px-5 sm:py-5"
      >
        {paystackSection}
      </div>
    </div>
  );
}

export default function PaymentMethodAccordion({
  paypalEnabled,
  paystackVisible,
  amountUsd,
  paystackQuote,
  paypalSection,
  paystackSection,
  defaultExpanded = 'paypal',
}) {
  const baseId = useId();
  const [expandedMethod, setExpandedMethod] = useState(defaultExpanded);
  const [showAlternatives, setShowAlternatives] = useState(false);

  function toggle(method) {
    setExpandedMethod((prev) => (prev === method ? prev : method));
  }

  const usdLabel = amountUsd != null ? `$${Number(amountUsd).toFixed(2)} USD` : '—';

  // PayPal off but Paystack available — don't hide Paystack behind collapse.
  if (!paypalEnabled && paystackVisible) {
    return (
      <div className="space-y-3">
        <PaystackPrimarySection
          baseId={baseId}
          paystackQuote={paystackQuote}
          paystackSection={paystackSection}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {paypalEnabled ? (
        <AccordionSection
          expanded={expandedMethod === 'paypal'}
          onToggle={() => toggle('paypal')}
          headerId={`${baseId}-paypal-header`}
          panelId={`${baseId}-paypal-panel`}
          header={
            <>
              <img
                src={paypalLogo}
                alt=""
                className="h-7 w-auto shrink-0 object-contain"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">PayPal or card</p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                      <span>USD</span>
                      <span className="text-slate-300" aria-hidden>·</span>
                      <ApplePayMark className="h-4" />
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-slate-900 pt-0.5">{usdLabel}</span>
                </div>
              </div>
            </>
          }
        >
          {paypalSection}
        </AccordionSection>
      ) : null}

      {paystackVisible && paypalEnabled ? (
        <MorePaymentOptionsSection
          expanded={showAlternatives}
          onToggle={() => setShowAlternatives((prev) => !prev)}
          headerId={`${baseId}-more-options-header`}
          panelId={`${baseId}-more-options-panel`}
        >
          {paystackSection}
        </MorePaymentOptionsSection>
      ) : null}
    </div>
  );
}
